import { Toast } from "primereact/toast";
import { createContext, useContext, useRef } from "react";

type ToastSeverity = "success" | "info" | "warn" | "error"; // <- Only these are allowed

interface ToastOptions {
  severity?: ToastSeverity;
  summary?: string;
  detail?: string;
  life?: number;
}

export const ToastContext = createContext<{
  showToast: (options: ToastOptions) => void;
  showErrorToast: (message: string) => void;
  showSuccessToast: (message: string) => void;
  showWarningToast: (message: string) => void;
} | null>(null);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const toastRef = useRef<Toast>(null);

  const showToast = ({
    severity = "success",
    summary = "",
    detail = "",
    life = 3000,
  }: ToastOptions) => {
    toastRef.current?.show({ severity, summary, detail, life });
  };

  const showErrorToast = (message: string) => {
    toastRef.current?.show({
      severity: "error",
      summary: "Error",
      detail: message,
      life: 3000,
    });
  };

  const showSuccessToast = (message: string) => {
    toastRef.current?.show({
      severity: "success",
      summary: "Success",
      detail: message,
      life: 3000,
    });
  };

  const showWarningToast = (message: string) => {
    toastRef.current?.show({
      severity: "warn",
      summary: "Warning",
      detail: message,
      life: 3000,
    });
  };

  return (
    <ToastContext.Provider
      value={{ showToast, showErrorToast, showSuccessToast, showWarningToast }}
    >
      <Toast ref={toastRef} position="top-right" />
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
};
